/**
 * MultiSessionDashboard - Advanced multi-browser session management UI
 * Provides tabbed interface for multiple concurrent browser containers
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
// import BrowserEmbed from './BrowserEmbed';  // VNC version - has noVNC loading issues
import MockBrowserEmbed from './MockBrowserEmbed';  // Working mock version
import { useSessionOrchestrator } from '../contexts/SessionOrchestrator';
import type { BrowserSession } from '../types/browser.types';

const MultiSessionDashboard: React.FC = () => {
  const {
    sessions,
    activeSession,
    createSession,
    terminateSession,
    switchSession,
    duplicateSession,
    isSpawning,
    terminateAllSessions,
    hibernateInactiveSessions
  } = useSessionOrchestrator();

  const [showNewTabModal, setShowNewTabModal] = useState(false);
  const [newTabUrl, setNewTabUrl] = useState('https://');
  const [showSettings, setShowSettings] = useState(false);

  // Tab icons
  const icons = {
    add: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    close: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    ),
    duplicate: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    settings: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    globe: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  const handleCreateSession = async () => {
    try {
      await createSession({
        env: { INITIAL_URL: newTabUrl }
      });
      setShowNewTabModal(false);
      setNewTabUrl('https://');
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const handleDuplicateTab = async (sessionId: string) => {
    try {
      await duplicateSession(sessionId);
    } catch (error) {
      console.error('Failed to duplicate session:', error);
    }
  };

  const getSessionStatus = (session: BrowserSession) => {
    switch (session.status) {
      case 'connected':
        return <span className="w-2 h-2 bg-ocean-green rounded-full animate-pulse" />;
      case 'connecting':
      case 'initializing':
        return <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />;
      case 'disconnected':
        return <span className="w-2 h-2 bg-gray-500 rounded-full" />;
      case 'error':
        return <span className="w-2 h-2 bg-red-500 rounded-full" />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-ocean-deep">
      {/* Tab Bar */}
      <div className="flex items-center bg-ocean-muted border-b border-ocean-cyan/20 px-2 py-1">
        {/* Session Tabs */}
        <div className="flex-1 flex items-center space-x-1 overflow-x-auto">
          {sessions.map((session) => (
            <motion.div
              key={session.id}
              layoutId={`tab-${session.id}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={clsx(
                'group flex items-center space-x-2 px-3 py-2 rounded-t-lg cursor-pointer',
                'transition-all duration-200 min-w-[150px] max-w-[250px]',
                activeSession?.id === session.id
                  ? 'bg-ocean-dark border-t border-l border-r border-ocean-cyan/30'
                  : 'bg-ocean-deep/50 hover:bg-ocean-deep/80 border-t border-l border-r border-transparent'
              )}
              onClick={() => switchSession(session.id)}
            >
              {/* Status Indicator */}
              <div className="flex-shrink-0">
                {getSessionStatus(session)}
              </div>

              {/* Tab Icon */}
              <div className="flex-shrink-0 text-ocean-cyan">
                {icons.globe}
              </div>

              {/* Tab Title */}
              <span className="flex-1 text-xs text-white truncate">
                Session {session.id.slice(0, 8)}
              </span>

              {/* Tab Actions */}
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicateTab(session.id);
                  }}
                  className="p-1 hover:bg-ocean-cyan/20 rounded"
                  title="Duplicate Tab"
                >
                  {icons.duplicate}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    terminateSession(session.id);
                  }}
                  className="p-1 hover:bg-red-500/20 rounded text-red-400"
                  title="Close Tab"
                >
                  {icons.close}
                </button>
              </div>
            </motion.div>
          ))}

          {/* New Tab Button */}
          <button
            onClick={() => setShowNewTabModal(true)}
            disabled={isSpawning || sessions.length >= 5}
            className={clsx(
              'flex items-center space-x-1 px-3 py-2 rounded-lg',
              'transition-all duration-200',
              isSpawning || sessions.length >= 5
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-ocean-cyan/20 hover:bg-ocean-cyan/30 text-ocean-cyan'
            )}
          >
            {isSpawning ? (
              <div className="w-4 h-4 border-2 border-ocean-cyan border-t-transparent rounded-full animate-spin" />
            ) : (
              icons.add
            )}
            <span className="text-xs">New Tab</span>
          </button>
        </div>

        {/* Tab Bar Actions */}
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={hibernateInactiveSessions}
            className="p-2 hover:bg-ocean-cyan/20 rounded transition-colors"
            title="Hibernate Inactive"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-ocean-cyan/20 rounded transition-colors"
            title="Settings"
          >
            {icons.settings}
          </button>
          {sessions.length > 0 && (
            <button
              onClick={terminateAllSessions}
              className="p-2 hover:bg-red-500/20 rounded transition-colors text-red-400"
              title="Close All Tabs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Browser Content Area */}
      <div className="flex-1 relative overflow-hidden">
        {activeSession ? (
          <MockBrowserEmbed
            key={activeSession.id}
            session={activeSession}
            showControls={true}
            showStats={showSettings}
            className="w-full h-full"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-6 text-ocean-cyan/30">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} 
                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-ocean-cyan mb-2">
                No Active Browser Sessions
              </h3>
              <p className="text-ocean-cyan/60 mb-6">
                Click "New Tab" to spawn a browser container
              </p>
              <button
                onClick={() => setShowNewTabModal(true)}
                disabled={isSpawning}
                className="px-6 py-3 bg-ocean-cyan text-ocean-deep rounded-lg 
                         hover:bg-ocean-green transition-colors font-medium
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSpawning ? 'Spawning...' : 'Create First Session'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Tab Modal */}
      <AnimatePresence>
        {showNewTabModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowNewTabModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-ocean-muted border border-ocean-cyan/30 rounded-lg p-6 w-96"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-medium text-ocean-cyan mb-4">
                New Browser Session
              </h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                handleCreateSession();
              }}>
                <input
                  type="url"
                  value={newTabUrl}
                  onChange={(e) => setNewTabUrl(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-ocean-deep border border-ocean-cyan/20 
                           text-white placeholder-ocean-cyan/50 focus:outline-none focus:border-ocean-cyan"
                  placeholder="Enter URL (optional)..."
                  autoFocus
                />
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowNewTabModal(false)}
                    className="px-4 py-2 bg-ocean-deep/50 text-ocean-cyan rounded-lg 
                             hover:bg-ocean-deep transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSpawning}
                    className="px-4 py-2 bg-ocean-cyan text-ocean-deep rounded-lg 
                             hover:bg-ocean-green transition-colors font-medium
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSpawning ? 'Creating...' : 'Create Session'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiSessionDashboard;
